import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, ShieldAlert, Lock, Target, IndianRupee, BarChart3, Sparkles, ArrowUpRight, ArrowDownRight, Building2, ChevronDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import AIPageInsight from "@/components/AIPageInsight";
import { PeriodBadge } from "@/components/Formatters";
import ActionPlaybook from "@/components/ActionPlaybook";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (n == null) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; if (a >= 1e3) return `${s}${(a/1e3).toFixed(1)}K`; return s + a.toLocaleString(); };
const AGING_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#F97316', '#EF4444'];
const cleanMd = (t) => t ? t.replace(/\*\*/g, '').replace(/\*/g, '').replace(/#{1,3}\s/g, '') : t;

const TrendIcon = ({ dir }) => dir === 'up' ? <TrendingUp className="h-4 w-4 text-emerald-500" /> : dir === 'down' ? <TrendingDown className="h-4 w-4 text-red-500" /> : <Minus className="h-4 w-4 text-slate-400" />;
const TrendColor = (dir) => dir === 'up' ? 'text-emerald-600' : dir === 'down' ? 'text-red-600' : 'text-slate-500';

export default function StrategicInsights() {
  const { period, refDate, token, currentMonthInfo } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiNarrative, setAiNarrative] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [playbookOpen, setPlaybookOpen] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [briefExpanded, setBriefExpanded] = useState(true);

  const openPlaybook = (insight) => { setSelectedInsight(insight); setPlaybookOpen(true); };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (refDate) params.ref_date = refDate;
      const res = await axios.get(`${API}/kpi/strategic-insights`, { headers: { Authorization: `Bearer ${token}` }, params });
      setData(res.data);
      // Fetch AI Intelligence Brief
      axios.post(`${API}/ai/insights?period=${period}${refDate ? `&ref_date=${refDate}` : ''}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => setAiInsights(r.data)).catch(() => {});
    } catch (e) { console.error(e); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token, period, refDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateNarrative = () => {
    if (!data || aiLoading) return;
    setAiLoading(true);
    const ctx = `Management Summary: Demand ${fmt(data.management_summary?.total_demand)}, Sales ${fmt(data.management_summary?.total_sales)}, Collection ${fmt(data.management_summary?.total_collection)}, Achievement ${data.management_summary?.achievement_pct}%. Trends: Demand ${data.trends?.demand?.direction} (${data.trends?.demand?.change_pct}%), Sales ${data.trends?.sales?.direction} (${data.trends?.sales?.change_pct}%), Collection ${data.trends?.collection?.direction} (${data.trends?.collection?.change_pct}%). Efficiency: Realization ${data.efficiency?.realization_rate?.value}%, Booking ${data.efficiency?.booking_strength?.value}%. Exceptions: ${data.exceptions?.length || 0} flagged. Blocked: ${data.total_blocked_units} units (${fmt(data.total_blocked_amount)}). Risks: ${data.management_summary?.key_risks?.join('; ')}`;

    axios.post(`${API}/ai/copilot`, {
      message: `Generate a concise strategic narrative. Include: (1) Business health in 2 lines, (2) Biggest risk and its consequence, (3) Top 3 actions with expected outcomes. Use Cr/L. Max 8 lines total.`,
      session_id: `strategic-${period}-${refDate}`,
      period, ref_date: refDate,
    }, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAiNarrative(cleanMd(r.data.response)))
      .catch(() => setAiNarrative("Unable to generate narrative."))
      .finally(() => setAiLoading(false));
  };

  const d = data || {};
  const ms = d.management_summary || {};
  const trends = d.trends || {};
  const eff = d.efficiency || {};
  const exceptions = d.exceptions || [];
  const aging = d.blocked_aging || [];
  const crossInsights = trends.cross_insights || [];

  return (
    <div className="space-y-4 sm:space-y-6 overflow-hidden" data-testid="strategic-insights-page">
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Strategic Insights</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
          Actionable intelligence for leadership decisions
        </p>
        <PeriodBadge period={period} currentMonthInfo={currentMonthInfo} />
      </div>

      {loading && <div className="text-center py-8"><div className="inline-block h-6 w-6 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" /></div>}

      {/* Executive Intelligence Brief (moved from Executive Command) */}
      {aiInsights && (
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="exec-ai-hero">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-xl bg-blue-50 flex items-center justify-center"><Sparkles className="h-4 w-4 text-blue-600" /></div>
                <div>
                  <CardTitle className="text-sm sm:text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Executive Intelligence Brief</CardTitle>
                  <p className="text-[9px] sm:text-[10px] text-slate-400 uppercase tracking-widest font-medium">AI-Powered Period Analysis</p>
                </div>
              </div>
              {aiInsights.risk_level && (
                <span className={`text-[10px] sm:text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${aiInsights.risk_level === 'high' ? 'bg-red-100 text-red-700' : aiInsights.risk_level === 'low' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="ai-risk-level">
                  {aiInsights.risk_level} Risk
                </span>
              )}
              <button onClick={() => setBriefExpanded(!briefExpanded)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors ml-2" data-testid="brief-collapse-btn">
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${briefExpanded ? '' : '-rotate-90'}`} />
              </button>
            </div>
          </CardHeader>
          {briefExpanded && (
          <CardContent className="pt-4 sm:pt-5">
            <p className="text-sm sm:text-base font-semibold text-slate-900 mb-2" data-testid="ai-headline" style={{ fontFamily: 'Manrope' }}>{cleanMd(aiInsights.headline)}</p>
            {aiInsights.summary && <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-4">{cleanMd(aiInsights.summary)}</p>}
            {aiInsights.categories?.length > 0 && (
              <div className="space-y-3 mb-4" data-testid="ai-categories">
                {aiInsights.categories.map((cat, i) => {
                  const sevColors = { critical: { border: 'border-l-red-500', bg: 'bg-red-50', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' }, warning: { border: 'border-l-amber-500', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' }, positive: { border: 'border-l-emerald-500', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' } };
                  const s = sevColors[cat.severity] || sevColors.warning;
                  return (
                    <div key={cat.name || i} className={`border border-slate-200/60 border-l-4 ${s.border} rounded-xl p-3 sm:p-4 ${s.bg}/30 cursor-pointer hover:shadow-md transition-shadow`} data-testid={`ai-cat-${i}`} onClick={() => openPlaybook({ title: cat.name, insight: cat.insight, metric: cat.metric_current, type: cat.severity })}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2"><div className={`h-2 w-2 rounded-full ${s.dot}`} /><span className="text-xs sm:text-sm font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>{cat.name}</span></div>
                        <div className="flex items-center gap-2">
                          {cat.change_pct != null && <span className={`text-[10px] sm:text-xs font-bold flex items-center gap-0.5 ${cat.change_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{cat.change_pct >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(cat.change_pct)}%</span>}
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${s.badge}`}>{cat.severity}</span>
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">{cleanMd(cat.insight)}</p>
                      <p className="text-[10px] text-blue-600 font-medium mt-1.5 flex items-center gap-1"><ArrowUpRight className="h-3 w-3" />Tap for Action Playbook</p>
                    </div>
                  );
                })}
              </div>
            )}
            {aiInsights.top_actions?.length > 0 && (
              <div className="pt-3 border-t border-slate-100" data-testid="ai-actions">
                <p className="text-[9px] sm:text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2">Recommended Actions</p>
                <div className="space-y-1.5">{aiInsights.top_actions.map((action, i) => <p key={`action-${i}`} className="text-xs sm:text-sm text-slate-700 flex items-start gap-2"><span className="text-emerald-500 font-bold flex-shrink-0">{i + 1}.</span>{cleanMd(action)}</p>)}</div>
              </div>
            )}
          </CardContent>
          )}
        </Card>
      )}

      {/* 7. MANAGEMENT SUMMARY — Always at top */}
      {ms.total_sales > 0 && (
        <Card className="bg-gradient-to-br from-[#0F1D32] to-[#1a2d4a] rounded-2xl border-0 text-white shadow-lg" data-testid="mgmt-summary">
          <CardContent className="p-5 sm:p-6">
            <p className="text-[10px] font-bold text-blue-300 uppercase tracking-[0.15em] mb-4">Management Summary</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 mb-4">
              <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Sales Target</p><p className="text-lg sm:text-xl font-bold mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(ms.target_value)}</p></div>
              <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Sales</p><p className="text-lg sm:text-xl font-bold mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(ms.total_sales)}</p></div>
              <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Collection</p><p className="text-lg sm:text-xl font-bold mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(ms.total_collection)}</p></div>
              <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Achievement</p><p className={`text-lg sm:text-xl font-bold mt-0.5 ${ms.achievement_pct >= 80 ? 'text-emerald-400' : 'text-amber-400'}`} style={{ fontFamily: 'Manrope' }}>{ms.achievement_pct}%</p></div>
              <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Coll Target</p><p className="text-lg sm:text-xl font-bold text-amber-400 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(ms.collection_target)}</p></div>
            </div>
            {ms.key_risks?.length > 0 && (
              <div className="pt-3 border-t border-white/10">
                <p className="text-[9px] text-red-300 uppercase font-bold tracking-wider mb-1.5">Key Risks</p>
                {ms.key_risks.map((r, i) => <p key={`risk-${i}`} className="text-xs text-slate-300 flex items-center gap-1.5 mb-0.5"><span className="h-1.5 w-1.5 rounded-full bg-red-400 flex-shrink-0" />{r}</p>)}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Strategic Narrative */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="ai-narrative">
        <CardHeader className="pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-600" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>AI Strategic Narrative</CardTitle></div>
            <button onClick={generateNarrative} disabled={aiLoading || loading} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold text-white bg-[#1D4ED8] hover:bg-[#1E40AF] rounded-lg transition-colors disabled:opacity-50" data-testid="generate-narrative-btn">
              {aiLoading ? <><div className="h-3 w-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Analyzing...</> : <><Sparkles className="h-3 w-3" /> Generate Narrative</>}
            </button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {aiNarrative ? (
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-line">{aiNarrative}</p>
          ) : (
            <p className="text-xs text-slate-400 text-center py-4">Click "Generate Narrative" for AI-powered strategic analysis with consequences and recommended actions</p>
          )}
        </CardContent>
      </Card>

      {/* 2. TREND ANALYSIS */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="trend-analysis">
        <CardHeader className="pb-3 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Trend Analysis (Month-on-Month)</CardTitle></CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {[
              { key: 'demand', label: 'Demand', d: trends.demand },
              { key: 'sales', label: 'Net Sales', d: trends.sales },
              { key: 'collection', label: 'Collection', d: trends.collection },
            ].map(({ key, label, d: t }) => t && (
              <div key={key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-600">{label}</span>
                  <div className="flex items-center gap-1"><TrendIcon dir={t.direction} /><span className={`text-xs font-bold ${TrendColor(t.direction)}`}>{t.change_pct > 0 ? '+' : ''}{t.change_pct}%</span></div>
                </div>
                <p className="text-base sm:text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{fmt(t.current)}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Previous: {fmt(t.previous)}</p>
              </div>
            ))}
          </div>

          {crossInsights.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Cross-Metric Intelligence</p>
              {crossInsights.map((ci, i) => (
                <div key={i} className={`p-3 rounded-xl border-l-4 cursor-pointer hover:shadow-md transition-shadow ${ci.type === 'danger' ? 'bg-red-50 border-l-red-500' : ci.type === 'warning' ? 'bg-amber-50 border-l-amber-500' : 'bg-emerald-50 border-l-emerald-500'}`} onClick={() => openPlaybook({ title: ci.title, insight: ci.insight, type: ci.type })}>
                  <p className={`text-xs font-bold ${ci.type === 'danger' ? 'text-red-700' : ci.type === 'warning' ? 'text-amber-700' : 'text-emerald-700'}`}>{ci.title}</p>
                  <p className="text-xs text-slate-700 mt-0.5 leading-relaxed">{ci.insight}</p>
                  <p className="text-[10px] text-blue-600 font-medium mt-1.5 flex items-center gap-1"><ArrowUpRight className="h-3 w-3" />Tap for Action Playbook</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3. EFFICIENCY METRICS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="efficiency-metrics">
        {[
          { key: 'realization_rate', label: 'Collection Efficiency', desc: 'Collection / Target', icon: IndianRupee, color: eff.realization_rate?.value >= 80 ? '#10B981' : eff.realization_rate?.value >= 60 ? '#F59E0B' : '#EF4444' },
          { key: 'booking_strength', label: 'Sales Achievement', desc: 'Sales / Target', icon: Target, color: eff.booking_strength?.value >= 80 ? '#10B981' : eff.booking_strength?.value >= 50 ? '#F59E0B' : '#EF4444' },
          { key: 'conversion_ratio', label: 'Net Conversion', desc: 'Net / Gross Sales', icon: BarChart3, color: eff.conversion_ratio?.value >= 90 ? '#10B981' : eff.conversion_ratio?.value >= 80 ? '#F59E0B' : '#EF4444' },
        ].map(({ key, label, desc, icon: Icon, color }) => {
          const m = eff[key] || {};
          return (
            <Card key={key} className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3"><div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}><Icon className="h-4 w-4" style={{ color }} /></div>
                  <div><p className="text-xs font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{label}</p><p className="text-[9px] text-slate-400">{desc}</p></div>
                </div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color }}>{m.value || 0}%</p>
                {m.previous != null && <p className="text-[10px] text-slate-400 mt-1">Previous: {m.previous}% ({m.value > m.previous ? <span className="text-emerald-600">improved</span> : m.value < m.previous ? <span className="text-red-600">declined</span> : <span>stable</span>})</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* 1. BLOCKED AGING (VALUE) */}
      {aging.some(a => a.units > 0) && (
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="blocked-aging">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-amber-500" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Blocked Units Aging (Value-Based)</CardTitle></div>
              <span className="text-xs font-bold text-amber-600">{d.total_blocked_units} units · {fmt(d.total_blocked_amount)}</span>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={aging} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="range" tick={{ fontSize: 10, fill: '#475569' }} />
                <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={fmt} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={(v, name) => [name === 'amount' ? fmt(v) : v, name === 'amount' ? 'Amount' : 'Units']} />
                <Bar dataKey="amount" name="Amount" radius={[6, 6, 0, 0]}>
                  {aging.map((_, i) => <Cell key={i} fill={AGING_COLORS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-5 gap-1 mt-3">
              {aging.map((a, i) => (
                <div key={i} className="text-center p-2 rounded-lg" style={{ background: `${AGING_COLORS[i]}10` }}>
                  <p className="text-xs font-bold" style={{ color: AGING_COLORS[i] }}>{a.units}</p>
                  <p className="text-[8px] text-slate-500">{a.range}</p>
                  <p className="text-[9px] font-semibold text-slate-700">{fmt(a.amount)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 5. EXCEPTION REPORTING */}
      {exceptions.length > 0 && (
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="exceptions">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-red-500" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Exception Reporting — Projects Requiring Attention</CardTitle></div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-2.5">
              {exceptions.map((ex, i) => (
                <div key={i} className={`p-3.5 rounded-xl border-l-4 cursor-pointer hover:shadow-md transition-shadow ${ex.severity === 'danger' ? 'bg-red-50/70 border-l-red-500' : 'bg-amber-50/70 border-l-amber-500'}`} data-testid={`exception-${i}`} onClick={() => openPlaybook(ex)}>
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{ex.project}</span>
                    </div>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${ex.severity === 'danger' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{ex.type}</span>
                  </div>
                  <p className="text-xs text-slate-600">{ex.detail} — <span className="font-semibold">{ex.metric}</span></p>
                  <p className="text-[10px] text-blue-600 font-medium mt-1.5 flex items-center gap-1"><ArrowUpRight className="h-3 w-3" />Tap for Action Playbook</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Playbook Modal */}
      <ActionPlaybook isOpen={playbookOpen} onClose={() => setPlaybookOpen(false)} token={token} period={period} refDate={refDate} insight={selectedInsight} />
    </div>
  );
}
